import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ArrowRight,
  Loader2,
  Search,
  ChevronDown,
  CheckCircle2,
  Mail,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { COUNTRIES, DEFAULT_COUNTRY } from "../../data/countries";
import { submitLead } from "../../lib/api";

function CountryPicker({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = useMemo(() => {
    if (!q) return COUNTRIES;
    const s = q.toLowerCase();
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(s) ||
        c.dial.includes(s) ||
        c.code.toLowerCase() === s
    );
  }, [q]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid={testid}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="h-full inline-flex items-center gap-2 px-3 text-sm font-medium text-charcoal hover:bg-zinc-50 border-r border-zinc-300"
      >
        <span className="text-base leading-none" aria-hidden>
          {value.flag}
        </span>
        <span className="font-mono">{value.dial}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180 text-electric" : "text-zinc-400"}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-full mt-1 w-72 bg-white border border-charcoal shadow-xl shadow-electric/20 rounded-xl z-[90]"
          >
            <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-200">
              <Search className="w-3.5 h-3.5 text-zinc-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search country or dial code…"
                className="flex-1 outline-none text-sm bg-transparent"
                aria-label="Search country"
                autoFocus
              />
            </div>
            <ul className="max-h-72 overflow-y-auto">
              {filtered.map((c) => (
                <li key={c.code}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(c);
                      setOpen(false);
                      setQ("");
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-zinc-50 ${
                      c.code === value.code ? "bg-zinc-50" : ""
                    }`}
                  >
                    <span className="text-base">{c.flag}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="font-mono text-xs text-zinc-500">
                      {c.dial}
                    </span>
                  </button>
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="px-3 py-4 text-xs text-zinc-500 text-center">
                  No country matches "{q}"
                </li>
              )}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * LeadCaptureModal — universal popup for resource downloads, audit delivery, webinar reservation, etc.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   eyebrow: string (e.g. "Free Audit Delivery")
 *   title: string (e.g. "Where should we send your full ASO audit?")
 *   description: string (subtitle copy)
 *   ctaLabel: string (button label, default "Send to me")
 *   successTitle: string (thank-you title)
 *   successBody: string (thank-you copy)
 *   source: string (lead source slug for analytics)
 *   extraPayload: object (extra fields merged into the POST)
 *   highlightChannels: ["email", "whatsapp"] | ["email"] etc. for thank-you bullets
 */
export default function LeadCaptureModal({
  open,
  onClose,
  eyebrow = "Get this resource",
  title = "Tell us where to send it.",
  description = "We'll email it to you instantly.",
  ctaLabel = "Send it to me",
  successTitle = "Sent!",
  successBody = "Check your inbox in the next 2 minutes.",
  source = "resource-download",
  extraPayload = {},
  highlightChannels = ["email", "whatsapp"],
}) {
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setSuccess(false);
      setForm({ name: "", email: "", phone: "" });
      setCountry(DEFAULT_COUNTRY);
    }
  }, [open]);

  if (!open) return null;
  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("That email doesn't look right.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: `${country.dial} ${form.phone.trim()}`,
        source,
        message: `Country: ${country.name}`,
        ...extraPayload,
      });
      setSuccess(true);
    } catch (err) {
      console.error(err);
      toast.error("Couldn't send. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center px-4"
      role="dialog"
      aria-modal="true"
      data-testid="lead-capture-modal"
    >
      <button
        type="button"
        aria-label="Close modal backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm"
      />
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="relative w-full max-w-lg bg-white border border-charcoal shadow-2xl shadow-electric/20 rounded-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-zinc-500 hover:text-charcoal z-10"
          aria-label="Close modal"
          data-testid="lead-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="p-7 md:p-9 text-center"
            data-testid="lead-modal-success"
          >
            <div className="w-16 h-16 mx-auto bg-electric text-white grid place-items-center mb-5 shadow-lg rounded-xl">
              <CheckCircle2 className="w-8 h-8" strokeWidth={2.5} />
            </div>
            <p className="text-overline text-electric mb-3">Thank you!</p>
            <h3 className="font-display text-2xl md:text-3xl font-extrabold text-charcoal leading-tight">
              {successTitle}
            </h3>
            <p className="mt-3 text-zinc-600 leading-relaxed">{successBody}</p>

            <ul className="mt-6 space-y-2.5 text-left max-w-sm mx-auto">
              {highlightChannels.includes("email") && (
                <li className="flex items-start gap-3 text-sm">
                  <span className="w-7 h-7 grid place-items-center bg-zinc-100 border border-zinc-200 flex-shrink-0">
                    <Mail className="w-3.5 h-3.5 text-electric" />
                  </span>
                  <span className="text-zinc-700">
                    Email arrives in 2 minutes from{" "}
                    <span className="font-mono">hello@lumotis.com</span>
                  </span>
                </li>
              )}
              {highlightChannels.includes("whatsapp") && (
                <li className="flex items-start gap-3 text-sm">
                  <span className="w-7 h-7 grid place-items-center bg-zinc-100 border border-zinc-200 flex-shrink-0">
                    <MessageCircle className="w-3.5 h-3.5 text-electric" />
                  </span>
                  <span className="text-zinc-700">
                    WhatsApp ping from our growth team within 24 hours.
                  </span>
                </li>
              )}
            </ul>

            <button
              type="button"
              onClick={onClose}
              data-testid="lead-modal-success-close"
              className="mt-8 inline-flex items-center gap-2 bg-charcoal text-white px-6 py-3 font-semibold hover:bg-electric transition-colors"
            >
              Got it <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="p-7 md:p-9">
            <p className="text-overline text-electric mb-3">{eyebrow}</p>
            <h3 className="font-display text-2xl md:text-3xl font-extrabold text-charcoal leading-tight">
              {title}
            </h3>
            <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
              {description}
            </p>

            <div className="mt-6 grid grid-cols-1 gap-4">
              <label className="block">
                <span className="text-overline text-zinc-500">Full name</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={onChange("name")}
                  placeholder="Priya Iyer"
                  data-testid="lead-input-name"
                  className="mt-2 w-full border border-zinc-300 px-4 py-3 text-sm focus:border-electric outline-none"
                />
              </label>
              <label className="block">
                <span className="text-overline text-zinc-500">Work email</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder="you@yourapp.com"
                  data-testid="lead-input-email"
                  className="mt-2 w-full border border-zinc-300 px-4 py-3 text-sm focus:border-electric outline-none"
                />
              </label>
              <div>
                <span className="text-overline text-zinc-500 block mb-2">
                  WhatsApp / phone
                </span>
                <div className="flex border border-zinc-300 focus-within:border-electric">
                  <CountryPicker
                    value={country}
                    onChange={setCountry}
                    testid="lead-country-picker"
                  />
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={onChange("phone")}
                    placeholder="98765 43210"
                    data-testid="lead-input-phone"
                    className="flex-1 px-4 py-3 text-sm outline-none min-w-0"
                  />
                </div>
                <p className="mt-1 text-[11px] text-zinc-500">
                  We'll send updates on WhatsApp from this number.
                </p>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="lead-modal-submit"
              aria-label={ctaLabel}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold hover:bg-electric-400 transition-colors disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  {ctaLabel} <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
            <p className="mt-3 text-xs text-zinc-500 text-center">
              No spam. Unsubscribe anytime. We email + WhatsApp once.
            </p>
          </form>
        )}
      </motion.div>
    </div>
  );
}
